import { GlassCard } from "@/components/ui/glass-card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AppWindow } from "lucide-react";
import { type Device } from "@/hooks/useDevices";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface InstalledApplicationsProps {
  device: Device;
}

const InstalledApplications = ({ device }: InstalledApplicationsProps) => {
  const isMobile = useIsMobile();

  const getAppName = (app: any): string => {
    if (!app.name) return 'Unknown App';
    return app.name.replace(/\.app$/, '');
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const getIconSrc = (icon: string | undefined): string | null => {
    if (!icon) return null;
    if (icon.startsWith('data:') || icon.startsWith('http')) return icon;
    return `data:image/png;base64,${icon}`;
  };

  const deviceApps = Array.isArray(device.installed_apps) ? device.installed_apps : [];
  const hasApps = deviceApps.length > 0;

  const sortedApps = [...deviceApps].sort((a, b) => {
    const nameCompare = (a.name || '').localeCompare(b.name || '');
    if (nameCompare !== 0) return nameCompare;
    return ((a.version || '') || '').localeCompare((b.version || '') || '');
  });

  const renderIcon = (app: any, size: "sm" | "md") => {
    const dim = size === "sm" ? "h-7 w-7" : "h-8 w-8";
    const iconDim = size === "sm" ? "h-4 w-4" : "h-5 w-5";
    if (app.icon && getIconSrc(app.icon)) {
      return (
        <img
          src={getIconSrc(app.icon) || ''}
          alt={`${getAppName(app)} icon`}
          className={cn(dim, "rounded flex-shrink-0 object-contain")}
        />
      );
    }
    return (
      <div className={cn(dim, "flex items-center justify-center flex-shrink-0")}>
        <AppWindow className={cn(iconDim, "text-info")} />
      </div>
    );
  };

  return (
    <GlassCard variant="default" hover={false} className={cn(isMobile ? "p-3" : "p-6")}>
      <h2 className={cn(
        "font-semibold text-slate-900 dark:text-white mb-3 sm:mb-4",
        isMobile ? "text-base" : "text-xl"
      )}>
        Installed Applications ({hasApps ? deviceApps.length : 0})
      </h2>

      {hasApps ? (
        isMobile ? (
          <div className="space-y-2">
            {sortedApps.map((app, index) => (
              <div
                key={`${app.name}-${app.version}-${index}`}
                className="flex items-center gap-2 p-3 rounded-lg border bg-card/50 min-h-[44px]"
              >
                {renderIcon(app, "sm")}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{getAppName(app)}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {app.version || 'N/A'} · {formatDate(app.installed_date)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-left">Application</TableHead>
                <TableHead className="text-left">Version</TableHead>
                <TableHead className="text-left">Installation Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedApps.map((app, index) => (
                <TableRow key={`${app.name}-${app.version}-${index}`}>
                  <TableCell className="flex items-center text-left">
                    <div className="mr-3">{renderIcon(app, "md")}</div>
                    <span className="font-medium">{getAppName(app)}</span>
                  </TableCell>
                  <TableCell className="text-left">{app.version || 'N/A'}</TableCell>
                  <TableCell className="text-left">{formatDate(app.installed_date)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )
      ) : (
        <div className={cn("text-center text-slate-500", isMobile ? "py-6" : "py-8")}>
          <AppWindow className={cn(
            "mx-auto mb-3 text-slate-400",
            isMobile ? "h-8 w-8" : "h-12 w-12"
          )} />
          <p className={cn("font-medium mb-2", isMobile ? "text-base" : "text-lg")}>
            No Applications Found
          </p>
          <p className="text-sm text-slate-400">
            This device hasn't reported any installed applications yet.
            Applications will appear here once they are detected during the next device check-in.
          </p>
        </div>
      )}
    </GlassCard>
  );
};

export default InstalledApplications;
